import { ChangeDetectorRef, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { resolveFormerCoachHashesAsync } from 'services/former-coach-resolver';
import { DEFAULT_COACH_NAME_FALLBACK } from '@cf-shared/data-model/coach';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CanLeaveResult, CsPage, NavController } from 'providers/nav-controller';
import { UserDb } from 'services/user-db.service';
import { CouchDocDraft, deepClone, deepCompareEQ, deepOverwrite, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { AuthService } from 'services/auth.service';
import { VldGroupDirective } from 'directives/vld-group/vld-group';
import { VldGroupState } from 'directives/vld-group/vld-group-interface';
import { DatabaseWrapper, DocWatcher, TransactionId } from 'services/database-wrapper';
import { CsHeaderButtons } from 'components/cs-header/cs-header';
import { LoggingService } from 'services/logging.service';
import { AnyExercise, CustomExercise, Exercise, ExerciseDbService, StrengthPerBW, StrengthStandard_Absolute, StrengthStandard_Relative, isCustomExerciseById, isForkUde, isOwnedCustomExercise, isStrengthStandardRelative, ExerciseDiffEntry, ImportedExerciseUpdateResult, ExerciseTextLanguage, ExerciseTextSaveError } from 'services/exercise-db.service';
import { ExerciseDiffField } from 'services/exercise-diff';
import { applyDiffFieldsToCanonical, pickForkSourceRev, snapshotDiffFields, resolveLocalCustomExerciseAsync } from 'services/exercise-fork-update';
import { PopupMenuButton, PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { IExerciseDiffPage, ExerciseDiffResult } from 'pages/exercise-diff/exercise-diff-page-interface';
import { buildLabeledCoachUpdateDiff } from 'services/exercise-diff-ui';
import { SettingsService } from 'services/settings.service';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { findNearestLiveAncestorAsync } from 'services/exercise-switching';
import { equipmentTemplatesEU } from 'pages/equipment/equipment-templates-eu';
import { localizeEquipmentTemplates } from 'pages/equipment/equipment-templates';
import { ImageService } from 'services/image-service';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { generateIdFromName } from '@cf-shared/shared-util'; // used for admin mode
import { generateCustomExerciseId } from '@cf-shared/data-model/exercise';
import { locateExerciseByIdAsync } from 'services/exercise-resolve';
import * as crypto from 'crypto';
import { AuthRole } from '@cf-shared/couch-auth';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ButtonType } from '@cf-shared/enums';
import { TelemetryService } from 'services/telemetry.service';
import { Loading, LoadingController } from 'providers/loading-controller';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { PreviewHelpText } from 'pages/help-card-page/help-card-page';
import { Equipment } from '@cf-shared/data-model/equipment';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { EditExercisePageParams } from './edit-exercise-interface';
import { IViewExercisePage } from 'pages/view-exercise/view-exercise-interface';
import { CoachExerciseSyncService } from 'services/coach-exercise-sync.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { ExerciseLoadOutcome, loadOutcomeForFailure } from './edit-exercise-load-outcome';
import { CloudFunctionNetworkError } from 'services/cloud-functions-errors';
import { isFirebaseAuthNetworkError } from 'services/error-patterns';
import { describeErrorForLog } from '@cf-shared/error-describe';

/**
 * The three modes this page edits in. They all share one draft lifecycle: a
 * `CouchDocDraft`, an `exerciseInitial` baseline to compare against, and the
 * save-or-discard flow built on that comparison.
 *
 * There used to be a fourth, `View`, which showed an exercise the user does not own with
 * every field disabled. It touched none of that machinery and is now its own page,
 * `ViewExercisePage` — see deferred finding `df-7556e3ddb615`.
 */
enum Mode {
  Custom,
  Admin,
  ForkOnEdit,
}

@Component({
    selector: 'page-edit-exercise',
    templateUrl: 'edit-exercise-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class EditExercisePage extends CsPage<EditExercisePageParams, string> implements OnDestroy {
  protected LL = LL;
  @ViewChild(VldGroupDirective) vldGroup: VldGroupDirective;

  genders = ['men', 'women'];
  levels = [{ name: 'Novice' }, { name: 'Intermediate' }, { name: 'Advanced' }];

  public isModified = false;
  isStrengthStandardRelative = isStrengthStandardRelative;

  Mode = Mode;

  protected title = '';
  protected mode: Mode;
  protected createMode = false;
  private docWatcher: DocWatcher;
  private coachAppliedSub: Subscription | null = null;
  // context for the ExerciseDiffPage modal's `onDiffPickerResult` returnCallback (the page
  // hands back only the picked action, so the source/rev are stashed here across the push).
  private pendingDiffApply: { source: CustomExercise; freshRev: string; diffLength: number } | null = null;
  protected exerciseId: string;
  protected exercise: AnyExercise;
  loading = true;
  isAdvancedExpanded = false;

  useRMTesting = false;

  private exerciseDraft: CouchDocDraft<AnyExercise>;
  protected exerciseInitial: AnyExercise;
  // captured pre-wipe in Mode.ForkOnEdit's loadAsync; read by saveAsync's fork branch.
  // exerciseInitial._rev is unusable for ForkOnEdit because exerciseInitial is snapshotted
  // AFTER loadAsync wipes _rev on the cloned global.
  private forkOnEditSourceId?: string;
  private forkOnEditSourceRev?: string;
  equipmentTemplates: Equipment[];

  allExercises: Exercise[]; // needed by the html to provide reference selections
  allCustomExercises: CustomExercise[];

  protected exerciseNotFound = false;
  // #4737: set when the exercise could not be READ at all — the database was shutting down
  // or recovering — as opposed to being genuinely gone. Kept apart from exerciseNotFound so
  // the two get different wording; telling a user their exercise no longer exists when we
  // simply could not look is what would make them recreate one they still have.
  protected exerciseUnreadable = false;
  protected isImportedExercise = false;
  protected hasOriginalUpdate = false;
  protected originalDeleted = false;
  // R3 refactor — `originalExercise` is no longer cached on the page; every consumer
  // does its own fresh fetch via `checkImportedExerciseForUpdateAsync` (the page-load
  // cache could be stale by minutes/hours by the time the user taps the banner). The
  // field is gone; reads/writes are removed at the call sites.

  // "Based on: <name>" line on forks. hasSourceLineage gates visibility; sourceExerciseName
  // is undefined while resolving and stays undefined when all three resolution paths miss
  // (line is omitted rather than rendering a raw-id fallback — chain-of-shares deep ancestor).
  protected hasSourceLineage = false;
  protected sourceExerciseName?: string;
  // Phase 3 of #3198 — the loaded UDE was authored by a coach the user has since
  // detached from. Surfaces a "Former coach" info card at the top of the page.
  // Independent of `hasSourceLineage` so from-scratch coach UDEs (no
  // `local.sourceExerciseId`) still display the indicator. See plans/coach-exercise-sharing-phase3.md §4.
  protected isFromFormerCoach = false;
  protected formerCoachName?: string;

  // Resolves a fork's source from the user's OWN DB so a same-user local source isn't
  // reported as a deleted shared exercise (#3808). Passed into every
  // `checkImportedExerciseForUpdateAsync` call below. Arrow field so it binds `this`.
  private resolveLocalSource = (id: string) => resolveLocalCustomExerciseAsync(this.userDb, id);

  get customExercise(): CustomExercise { return this.exercise as CustomExercise; }

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    public auth: AuthService,
    private logging: LoggingService,
    private loadingCtrl: LoadingController,
    private imageService: ImageService,
    private exerciseDB: ExerciseDbService,
    private alertCtrl: AlertController,
    private telemetry: TelemetryService,
    private cloudFunctionsService: CloudFunctionsService,
    private toastController: ToastController,
    private settings: SettingsService,
    private cdRef: ChangeDetectorRef,
    private popupMenuCtrl: PopupMenuProvider,
    private coachExerciseSync: CoachExerciseSyncService,
  ) {
    super();
  }

  // Reports what it found instead of setting the page's error flags. ionViewWillEnter is
  // the one place that turns this answer into the card the user sees.
  async loadAsync(): Promise<ExerciseLoadOutcome> {
    try {
      if (this.mode === Mode.ForkOnEdit) {
        // AUTHORING: the fork COPIES this text into a custom exercise, and custom
        // exercises are never translated (#4210). Forking in Spanish must not
        // smuggle Spanish text into the untranslated corpus — and the fork's
        // later diff against this same source has to compare like with like.
        const global = await this.exerciseDB.getGlobalExerciseByIdOrThrowAsync(
          this.exerciseId, this.docWatcher, ExerciseTextLanguage.Authoring);
        if (!global) {
          return ExerciseLoadOutcome.NotFound;
        }
        // capture source identity FIRST, before any mutation — exerciseInitial._rev
        // would be undefined here because the snapshot in ionViewWillEnter happens
        // AFTER loadAsync wipes _rev below
        this.forkOnEditSourceId = global._id;
        this.forkOnEditSourceRev = global._rev;
        const fork = deepClone(global) as CustomExercise;
        fork._rev = undefined;
        fork.authorHash = undefined;
        fork.usedInSlots = [];
        this.exercise = fork;
        this.exerciseDraft = new CouchDocDraft(this.exercise);
        this.exercise = this.exerciseDraft.draft;
        return ExerciseLoadOutcome.Loaded;
      }
      if (this.mode === Mode.Admin) {
        // pin English: English is the authoring locale for curated exercise text
        // (#4210), so the admin editor must load and save back the SAME locale.
        // Without the pin, an admin running the app in Spanish would load the
        // translated text and then save it over the English `$text/` documents.
        this.exercise = await this.exerciseDB.getGlobalExerciseByIdOrThrowAsync(
          this.exerciseId, this.docWatcher, ExerciseTextLanguage.Authoring);
      } else {
        // getCustomExerciseByIdAsync THROWS on a missing document and never answers with
        // an empty one, so in this mode the guard below is a backstop and the catch is
        // where a deleted exercise really arrives.
        this.exercise = await this.userDb.getCustomExerciseByIdAsync(this.exerciseId);
      }
      // one guard for both readers above: neither of them threw, and neither gave us an
      // exercise, so there is nothing to edit
      if (!this.exercise) {
        return ExerciseLoadOutcome.NotFound;
      }
      this.exerciseDraft = new CouchDocDraft(this.exercise);
      this.exercise = this.exerciseDraft.draft;
      return ExerciseLoadOutcome.Loaded;
    } catch (e) {
      // Do NOT rethrow: that aborts ionViewWillEnter before it clears the loading flag,
      // which is exactly the never-ending spinner of #4737.
      //
      // A not-found is an expected state, and so is a database that is shutting down or
      // recovering — maybeAutoPublishToCoachChannelAsync below swallows that same type for
      // the same reason. Anything else is a real surprise and must reach a future debugger
      // rather than stop at a card the user sees.
      if (!isPouchNotFound(e) && !isDatabaseUnavailable(e)) {
        this.logging.trackAbnormalException(e);
      }
      // Only report a failure when there is no exercise to show, so that a failure
      // arriving after the exercise has loaded cannot draw the exercise and a "this is
      // gone" card on the same screen.
      if (this.exercise) {
        return ExerciseLoadOutcome.Loaded;
      }
      // which card: the rule, and why a curated read is judged differently, is in
      // loadOutcomeForFailure
      return loadOutcomeForFailure(e, this.mode !== Mode.Custom);
    }
  }

  // Returns true when the caller must NOT call popAsync afterwards — either the
  // save navigated itself, or it FAILED and the editor deliberately stays open
  // so the edit is not lost behind a page that closed as though it had worked.
  async saveAsync(): Promise<boolean> {
    if (this.mode === Mode.Admin) {
      if (!await this.alertCtrl.showAlertAsync({
        title: LL.editExercise.alerts.saveAndExit.title(),
        message: LL.editExercise.alerts.saveAndExit.message(),
        buttons: [
          { text: LL.common.buttons.yes(), role: AlertButtonRole.Primary, result: true, },
          { text: LL.common.buttons.no(), role: AlertButtonRole.Cancel, result: false },
        ]
      })) {
        return false;
      }
      const canonical = this.exerciseDraft.commitDraft();
      // in create mode, _id was generated on the draft (in nameChanged) but commitDraft
      // uses deepOverwriteCouchSafe which preserves canonical's original empty _id
      if (this.createMode) canonical._id = this.exercise._id;
      // AWAIT this one. Saving a curated exercise is no longer a single write:
      // it also writes the three `$text/en-us/@exercise:` documents that are now
      // what everyone READS (#4210). Left fire-and-forget, a failure part-way
      // would be an unhandled rejection — no report, no message — and the admin
      // would see their edit apparently do nothing, because the exercise
      // document had been saved while the `$text/` document had not.
      try {
        await this.exerciseDB.saveExerciseAsync(canonical, DatabaseWrapper.getTransactionId());
      } catch (err) {
        // commitDraft already merged the edits into this page's copy of the
        // exercise (saveExerciseAsync puts the stored object itself back). Put
        // the copy back when NOTHING reached the database, or the page would go
        // on showing text that was never saved. The draft keeps the edits, so a
        // retry still works.
        //
        // ExerciseTextSaveError is the exception: there the exercise document
        // itself WAS stored and only its `$text/` siblings failed, so the
        // canonical already matches the database and rolling it back would
        // introduce the very disagreement this is guarding against.
        // deepOverwriteCouchSafe, not deepOverwrite: the canonical keeps its own
        // `_id` / `_rev` / `db__*`, which may have moved on since the page opened.
        if (!(err instanceof ExerciseTextSaveError)) {
          deepOverwriteCouchSafe(canonical, this.exerciseInitial);
        }
        // report the ORIGINAL error, not the wrapper: the database-recovery path
        // inside trackAbnormalException inspects the error object itself (an
        // IndexedDB failure has to be recognized as one), and Sentry captures the
        // stack of whatever it is handed
        const cause = err instanceof ExerciseTextSaveError ? err.reason : err;
        this.logging.trackAbnormalException(cause, LL.editExercise.toasts.saveFailed());
        // return TRUE so the caller does NOT pop the page. The save failed, and
        // closing the editor anyway would read as success and lose the edit.
        return true;
      }
      return false;
    }

    if (this.mode === Mode.ForkOnEdit) {
      return this.forkAndSaveAsync();
    }

    if (this.mode === Mode.Custom) {
      this.exercise.name = this.exercise.name.trim();
      const isOwn = this.isOwnedByCurrentUser(this.exerciseInitial);
      // foreign UDE → fork; otherwise in-place save
      if (!this.createMode && !isOwn) {
        return this.forkAndSaveAsync();
      }
      const canonical = this.exerciseDraft.commitDraft();
      if (this.createMode) canonical._id = this.exercise._id;
      await this.userDb.saveCustomExerciseAsync(canonical as CustomExercise, DatabaseWrapper.getTransactionId());
      // auto-publish if shared
      if ((canonical as CustomExercise).local?.shared) {
        void this.cloudFunctionsService.publishExerciseAsync({
          exercise: canonical as CustomExercise,
          adminMode: false,
        });
      }
      // auto-publish to coach channel — fire-and-forget; server-side validation is the
      // authoritative gate (the client check is a UX fast-path to skip pointless calls).
      void this.maybeAutoPublishToCoachChannelAsync(canonical as CustomExercise);
      return false;
    }

    return false;
  }

  // Phase 2 of #3198 auto-publish: when a coach saves an owned UDE and has at least one
  // claimed client, publish the exercise to the
  // coach channel so it auto-replicates into every claimed client's local DB. Independent
  // of `local.shared` — a UDE may be on the coach channel only, the public channel only,
  // both, or neither. Fire-and-forget; transient errors log and drop. Server rejection
  // (no claimed clients due to sync lag) is an expected outcome, not an error.
  private async maybeAutoPublishToCoachChannelAsync(exercise: CustomExercise): Promise<void> {
    // A background task must not reject: a rejected `void`-ed promise becomes an
    // unhandled rejection. `hasClaimedClientsAsync` reads the database and now
    // THROWS when it is unavailable (deferred #2) rather than answering with an
    // empty list, and the honest answer to "does this user have claimed clients"
    // is then "unknown" — so skip the channel work rather than guess either way.
    // It resumes on the next edit once the database is back.
    let hasClaimedClients: boolean;
    try {
      hasClaimedClients = await this.userDb.hasClaimedClientsAsync();
    } catch (err) {
      if (!isDatabaseUnavailable(err)) throw err;
      return;
    }
    if (!hasClaimedClients) return;
    if (!this.isOwnedByCurrentUser(exercise)) return;
    // narrow try to the cloud call — sync-lag rejections and transient network errors
    // here are expected (server is the authoritative validator). Programmer errors above
    // (config read, claimed-clients check) propagate as real bugs. The cloud function
    // returns `{ error: '...' }` as a 200-OK body — NOT a thrown error — so server-side
    // rejections land on `result.error`, not on `err`.
    try {
      const result = await this.cloudFunctionsService.publishExerciseToCoachChannelAsync({ exercise });
      if (result?.error) {
        const isExpectedSyncLag = result.error.includes('Coach channel requires at least one claimed client');
        if (isExpectedSyncLag) {
          console.log('coach-channel auto-publish skipped (sync-lag: no claimed clients yet)', result.error);
        } else {
          console.error('coach-channel auto-publish rejected by server', result.error);
          this.logging.trackSilentProblem('coach-channel auto-publish rejected by server', { docId: exercise._id, serverError: result.error });
        }
      }
    } catch (err: any) {
      // Reached only on network / runtime errors after the cloud-functions resilient-retry
      // chain is exhausted. Surface — silent retries would keep the coach channel out of
      // sync on every save until the next manual re-save.
      console.error('coach-channel auto-publish failed', err);
      this.logging.trackSilentProblem('coach-channel auto-publish failed', { docId: exercise._id, err: String(err?.message ?? err), stack: err?.stack });
    }
  }

  // own UDE = author hashSource prefix matches current uid, OR a bare legacy UDE with no
  // hashSource and no foreign markers (the user's own, created before the stamp existed —
  // issue #3808). Globals + foreign UDEs are NOT owned and fork on save. Delegates to the shared predicate so the rule is one
  // place and unit-tested.
  private isOwnedByCurrentUser(ex: AnyExercise): boolean {
    return isOwnedCustomExercise(ex, this.auth.user?.uid);
  }

  private async forkAndSaveAsync(): Promise<boolean> {
    // capture uid once — defends against logout race between guard and use
    const uid = this.auth.user?.uid;
    if (!uid) {
      this.toastController.showToast({
        message: LL.editExercise.toasts.saveFailed(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return false;
    }
    const transactionId = DatabaseWrapper.getTransactionId();

    // resolve source identity. ForkOnEdit captured pre-wipe in loadAsync; for Mode.Custom
    // foreign, exerciseInitial is the un-mutated source UDE doc.
    let sourceId: string;
    let sourceRev: string | undefined;
    if (this.mode === Mode.ForkOnEdit) {
      sourceId = this.forkOnEditSourceId;
      sourceRev = this.forkOnEditSourceRev;
    } else {
      sourceId = this.exerciseInitial._id;
      // R1 part 3 — pure helper picks the staging-DB rev (coachChannelRevision ??
      // sourceRevision ?? _rev) so the fork's lineage baseline is in the same rev
      // sequence as `checkImportedExerciseForUpdateAsync`'s comparison target. Coach
      // and public-share fork paths converge here.
      sourceRev = pickForkSourceRev(this.exerciseInitial as CustomExercise);
    }

    try {
      this.exercise.name = this.exercise.name.trim();
      // deep-clone the draft (not the canonical) for the fork — for Mode.Custom foreign
      // the canonical is the source UDE in the identity map, and committing the draft
      // would pollute it with the user's edits (which are NEVER saved back to the source).
      // Cloning the draft directly captures user edits without touching the canonical.
      const fork = deepClone(this.exercise) as CustomExercise;
      fork._rev = undefined;
      fork.authorHash = undefined;
      fork.usedInSlots = [];
      // FULL overwrite of local — a foreign-UDE source carries shared/hashSource that
      // must NOT inherit (forks start private + hashSource must be current user's)
      fork.local = { sourceExerciseId: sourceId };
      if (sourceRev) fork.local.sourceRevision = sourceRev;
      // 3-way-diff base: snapshot the SOURCE's diffable fields at fork time (the
      // un-mutated `exerciseInitial`, NOT the user-edited draft). Future update pickers
      // diff coach-changes against this base, so the user's own fork-time edits (e.g. a
      // rename) are never re-offered as "updates".
      fork.local.coachBaseFields = snapshotDiffFields(this.exerciseInitial as CustomExercise);
      // preserves sourceExerciseId/sourceRevision (helper does local = local || {}),
      // writes hashSource = uid:ISO_now, regenerates _id = exercise/+UDE-<sha1>
      generateCustomExerciseId(uid, fork, crypto);

      await this.userDb.saveCustomExerciseAsync(fork, transactionId);
      // forks count as user-authored UDEs — auto-publish to coach channel if applicable.
      void this.maybeAutoPublishToCoachChannelAsync(fork);

      // stat migration — deep-clone source's stat under fork id (preserves
      // rep-target preferences, weight history, myoreps preference, RM-testing
      // preference, custom rest seconds). Mutate the singleton object directly.
      const sourceStat = this.userDb.fitnessState.exerciseStats?.[sourceId];
      if (sourceStat && this.userDb.fitnessState.exerciseStats) {
        this.userDb.fitnessState.exerciseStats[fork._id] = deepClone(sourceStat);
        await this.userDb.saveFitnessStateAsync(transactionId);
      }

      // active-plan rewrite — fork is a rename, not a swap; bypasses
      // replaceExerciseInSubSlot via the helper to preserve subslot path state
      await this.rewritePlanExerciseIdAsync(sourceId, fork._id, transactionId);

      this.toastController.showToast({
        message: LL.editExercise.toasts.exerciseCustomized(),
        type: ToastType.SuccessLight,
        duration: 2000,
      });
      // popAsync(fork._id) returns to the parent page (e.g. exercise-switch picker)
      // and fires its returnCallback with the fork id — picker's onExerciseModified
      // sets selectExerciseByIdWhenLoading + patches its plan snapshot's subSlot to
      // the fork. Earlier design used replaceTopAsync to land on the fork's edit
      // page, but that left the user on a no-modification view of an exercise they
      // just created with only an X (cancel) button — popping straight back to the
      // picker is the natural place to confirm the fork landed and pick something
      // else if needed.
      await this.navCtrl.popAsync(fork._id);
      return true;
    } catch (err) {
      console.error('Fork-on-save failed', err);
      this.toastController.showToast({
        message: LL.editExercise.toasts.saveFailed(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return false;
    }
  }

  async ionViewCanEnter() {
    const mode = this.navParams.data.mode || '';
    this.exerciseId = this.navParams.data.id;

    if (!this.docWatcher) {
      this.docWatcher = this.exerciseDB.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdRef.markForCheck();
      });
    }
    if (!this.coachAppliedSub) {
      // live-refresh the "Update Available" banner: a coach edit lands via
      // coach-exercise-sync's apply pipeline (a same-device local write the page
      // docWatcher doesn't surface as an update signal), so re-detect on each apply.
      // debounce because one coach edit can apply several docs in quick succession.
      this.coachAppliedSub = this.coachExerciseSync.coachExerciseApplied$
        .pipe(debounceTime(500))
        .subscribe(() => {
          if (this.viewDestroyed) return;
          // only a fork being edited (Custom mode) has an "Update Available" banner to refresh
          if (this.mode !== Mode.Custom || !this.isImportedExercise) return;
          void this.checkForOriginalUpdateAsync();
        });
    }

    if (mode === 'forkOnEdit') {
      this.mode = Mode.ForkOnEdit;
      this.createMode = false;
      return;
    }

    this.createMode = this.exerciseId === undefined || this.exerciseId === '';
    this.isModified = this.createMode;  // in create mode, we only support the apply button
    if (!this.createMode && isCustomExerciseById(this.exerciseId)) {
      this.mode = Mode.Custom;
    } else {
      if (mode === 'admin') {
        await this.auth.pageAuthGuardAsync(AuthRole.Dev);
        this.mode = Mode.Admin;
      } else {
        this.mode = Mode.Custom;
      }
    }
  }

  async ionViewWillEnter() {
    this.loading = true;
    // #4737: clear both error flags before re-loading. The page is re-entered (returning
    // from a preview, from the "view original" link), and a flag left set by an earlier
    // failed load would keep drawing its card over an exercise that loaded perfectly well
    // the second time.
    this.exerciseNotFound = false;
    this.exerciseUnreadable = false;

    // Navigation history is now handled automatically by trail-based navigation

    if (this.createMode) {
      // only initialize draft on first enter (preserve data when returning from preview)
      if (!this.exerciseDraft) {
        const newExercise: AnyExercise = {
          usedInSlots: [],
          name: '',
          shortname: '',
          description: '',
          bw_pc: 0,
          allowMyoReps: true,
          _id: '',
        };
        this.exerciseDraft = new CouchDocDraft(newExercise);
        this.exercise = this.exerciseDraft.draft;
      }
    } else {
      const outcome = await this.loadAsync();
      // #4737: no exercise to show — draw an error card and finish loading. Everything
      // below assumes there is an exercise (the title on the line after this block reads
      // its name), and throwing there leaves the page on its spinner for ever, because the
      // line that clears the flag is never reached. Every mode can land here: a curated or
      // deleted id in Custom mode, and a missing exercise in Admin and fork-on-edit mode.
      if (outcome !== ExerciseLoadOutcome.Loaded) {
        this.exerciseNotFound = outcome === ExerciseLoadOutcome.NotFound;
        this.exerciseUnreadable = outcome === ExerciseLoadOutcome.Unreadable;
        this.loading = false;
        this.cdRef.markForCheck();
        return;
      }
      // check for updates on imported exercises (fire-and-forget)
      if (this.mode === Mode.Custom) {
        this.isImportedExercise = !!this.customExercise?.local?.sourceExerciseId;
        if (this.isImportedExercise) {
          // resolve "Based on: <name>" lineage — only when src !== _id (excludes
          // import-self-reference, where it would be a tautology)
          const src = this.customExercise.local.sourceExerciseId;
          if (src !== this.customExercise._id) {
            this.hasSourceLineage = true;
            void this.resolveSourceExerciseNameAsync(src);
          }
          void this.checkForOriginalUpdateAsync();
        }
        // Phase 3: check whether this UDE belongs to a former coach. Independent
        // of source lineage — from-scratch coach UDEs must show the badge too.
        void this.resolveFormerCoachAsync();
      }
    }
    // cs-textarea / cs-input two-way bindings write '' onto undefined string fields
    // during initial render. deepCompareEQ treats '' !== undefined (they have different
    // typeof after its undefined→false normalization), so without this step a user who
    // never edits anything (or who types and reverts) hits a false-positive diff at
    // exitPageAsync's deepCompareEQ guard and a fork gets created. Normalize potentially-
    // bound string fields to '' on both the draft and the baseline so the post-binding
    // shape is the comparison baseline. Mirrors the createMode init at ionViewWillEnter
    // which already populates these explicitly.
    this.normalizeBoundStringFields(this.exercise);
    // copy on load, so we can properly cancel, since fitness state is a global persistent singleton!
    if (!this.exerciseInitial) {
      this.exerciseInitial = deepClone(this.exercise);
    }
    this.normalizeBoundStringFields(this.exerciseInitial);
    this.title = this.createMode ? LL.editExercise.titles.createExercise() : LL.editExercise.titles.editExercise({ name: this.exercise.name });
    // the equipment picker shows LOCALIZED template names even in admin mode: the
    // picker stores `equipmentTemplateId`, never the name, so the label is ordinary
    // UI text. only the exercise TEXT below is pinned to the authoring locale,
    // because that is what an admin edits and saves back.
    this.equipmentTemplates = localizeEquipmentTemplates(equipmentTemplatesEU);
    // in admin mode this list feeds the reference/similar-exercise pickers on a
    // page that saves back to the authoring locale, so pin English there too
    this.allExercises = await this.exerciseDB.getAllExercisesAsync(undefined,
      this.mode === Mode.Admin ? ExerciseTextLanguage.Authoring : ExerciseTextLanguage.Active);
    this.allCustomExercises = await this.userDb.getAllCustomExercisesAsync();
    this.loading = false;
    this.cdRef.markForCheck();
  }

  async ionViewDidEnter() {
    if (this.createMode) {
    }
  }

  nameChanged() {
    // if never saved before, generate the exercise ID
    if (this.exercise._rev == null && this.createMode) {
      if (this.mode === Mode.Admin) {
        this.exercise._id = 'exercise/$' + generateIdFromName(this.exercise.name, '_');
      } else if (!this.exercise._id) {
        // generate SHA-based ID once (stable for sharing, no name collisions)
        generateCustomExerciseId(this.auth.user.uid, this.exercise as CustomExercise, crypto);
      }
    }

    this.somethingChanged();
  }

  isValid(): boolean {  // TODO: unused??
    return this.vldGroup.state === VldGroupState.Ready || this.vldGroup.state === VldGroupState.Done;
  }

  // used by [validity] property in vld-error component in the form
  validity(field: string | number): string {
    if (field === 'name') {
      if (this.exercise.name === '') {
        return LL.editExercise.errors.invalidName();
      }
      return null;
    }

    return null;
  }

  somethingChanged() {
    this.isModified = !deepCompareEQ(this.exercise, this.exerciseInitial) || this.createMode;
    this.cdRef.markForCheck();
  }

  toggleAdvanced() {
    this.isAdvancedExpanded = !this.isAdvancedExpanded;
    this.cdRef.markForCheck();
  }

  // app-initiated pop passes the id explicitly via popAsync(id); browser/system
  // back goes through this hook. Returning the current exercise id keeps the
  // picker's onExerciseModified working in both cases — without this, browser
  // back from the post-fork edit page leaves selectExerciseByIdWhenLoading
  // undefined and the picker doesn't auto-select / mark Active.
  // if the exercise is still being created, there is nothing to hand back: nameChanged gives
  // it an id as soon as a name is typed, but no document exists until Save, and Save pops
  // with the id explicitly. Handing back that id made the picker read a document that was
  // never written, and the unowned not_found reached Sentry (#6267).
  async ionViewCanLeave(): Promise<CanLeaveResult<string>> {
    if (this.createMode) return true;
    return { result: this.exercise?._id };
  }

  async exitPageAsync(opts: { save: boolean }) {
    if (opts.save && !deepCompareEQ(this.exercise, this.exerciseInitial)) {
      const navHandled = await this.saveAsync();
      if (navHandled) return;
    } else {
      deepOverwrite(this.exercise, this.exerciseInitial);
    }

    // pass exercise ID to popAsync — NavController will invoke returnCallback on parent
    // (callback must come after saving, since saving sets up global exercise setup for this user)
    // optional chain because an exercise that could not be loaded leaves this empty, and the
    // page now stays open on the "no longer available" card, so the back button has to work
    // from that state too (#4737).
    await this.navCtrl.popAsync(this.exercise?._id);
  }

  async handleAcceptButton() {
    return this.exitPageAsync({ save: true });
  }

  async handleCancelButton() {
    return this.exitPageAsync({ save: false });
  }

  getVisibleButtons(): CsHeaderButtons {
    if (this.isModified) {
      return CsHeaderButtons.Accept | CsHeaderButtons.Cancel;
    } else {
      if (this.createMode) {
        return CsHeaderButtons.Cancel;
      } else {
        return CsHeaderButtons.Back;
      }
    }
  }

  ButtonType = ButtonType;
  CsHeaderButtons = CsHeaderButtons;

  handleBackButtonClicked(buttonType: ButtonType) {
    this.telemetry.trackEvent('edit_exercise_back_button_click', { buttonType: buttonType });
    void this.handleCancelButton();
  }

  handleAcceptButtonClicked(event: Event | null, buttonType: ButtonType) {
    this.telemetry.trackEvent('edit_exercise_accept_button_click', { buttonType: buttonType });
    if (event) {
      lockClick(event, this.handleAcceptButton());
    } else {
      void this.handleAcceptButton();
    }
  }

  handleCancelButtonClicked(event: Event | null, buttonType: ButtonType) {
    this.telemetry.trackEvent('edit_exercise_cancel_button_click', { buttonType: buttonType });
    if (event) {
      lockClick(event, this.handleCancelButton());
    } else {
      void this.handleCancelButton();
    }
  }

  isButtonVisible(button: CsHeaderButtons) {
    return (this.getVisibleButtons() & button) as CsHeaderButtons === button;
  }

  handleButtonClicked(button: CsHeaderButtons) {
    switch (button) {
      case CsHeaderButtons.Back:
        this.handleBackButtonClicked(ButtonType.Header);
        break;
      case CsHeaderButtons.Cancel:
        this.handleCancelButtonClicked(null, ButtonType.Header);
        break;
      case CsHeaderButtons.Accept:
        this.handleAcceptButtonClicked(null, ButtonType.Header);
        break;
      default:
        this.logging.trackAbnormalError(LL.editExercise.errors.unknownButton({ button }));
    }
  }

  // resolve the source's display name for the "Based on: ..." line, through the same
  // lookup ViewExercisePage loads with. If it finds nothing, leave sourceExerciseName
  // unset — the line falls back gracefully (template guards on it).
  private async resolveSourceExerciseNameAsync(sourceId: string) {
    try {
      const located = await locateExerciseByIdAsync(
        sourceId, this.exerciseDB, this.userDb, this.cloudFunctionsService, this.docWatcher);
      if (this.viewDestroyed) return;
      if (located) {
        this.sourceExerciseName = located.exercise.name;
        this.cdRef.markForCheck();
      }
    } catch (err) {
      console.log('resolveSourceExerciseName failed', err);
      // a missing source, a local database that could not be read (shutting down or
      // recovering), and a cloud call that failed for lack of network are all expected here:
      // the line just stays blank. Anything else is a real fault and must reach Sentry.
      // test the wrapper type, not isNetworkError: that also matches any bare TypeError, and a
      // TypeError from the local reads here is a bug, not a lost connection. A fetch that fails
      // for lack of network (timeouts included) arrives wrapped once the callable's retries run
      // out. since #6119 that includes the sign-in token refresh the callable does before the
      // fetch (Firebase's auth/network-request-failed); the raw check stays for any other path.
      if (!isPouchNotFound(err) && !isDatabaseUnavailable(err)
        && !(err instanceof CloudFunctionNetworkError) && !isFirebaseAuthNetworkError(err)) {
        this.logging.trackSilentProblem('edit-exercise: resolveSourceExerciseName failed', {
          sourceId, error: describeErrorForLog(err),
        });
      }
    }
  }

  // Phase 3 of #3198: mark this page when the loaded UDE was authored by a
  // former coach. Mirrors the picker's resolver (services/former-coach-resolver.ts)
  // and excludes the active coach from the former-coach set so a re-claim during
  // sync-lag doesn't tag an active-coach UDE.
  private async resolveFormerCoachAsync(): Promise<void> {
    try {
      const hash = (this.exercise as CustomExercise)?.authorIdentityHash;
      if (!hash) return;
      const rel = this.userDb.loadCoachRelAsClient();
      const { hashToCoachName } = await resolveFormerCoachHashesAsync(rel?.formerCoaches, rel?.coachUid);
      if (this.viewDestroyed) return;
      const matchedName = hashToCoachName.get(hash);
      if (matchedName) {
        this.isFromFormerCoach = true;
        this.formerCoachName = matchedName;
        this.cdRef.markForCheck();
      }
    } catch (err: any) {
      console.error('edit-exercise: resolveFormerCoach failed', err);
      this.logging.trackSilentProblem('edit-exercise: resolveFormerCoach failed', { err: String(err?.message ?? err), stack: err?.stack });
    }
  }

  // Phase 3 of #3198 — body text for the "Former coach" info card. Uses a
  // dedicated editPageBody i18n key (NOT the picker's tooltip text, which
  // ends with "pick a different version" — incongruous on the edit page).
  // Falls back to the literal 'Coach' + surfaces if formerCoachName is unset
  // when the card is rendered (contract violation: resolveFormerCoachAsync
  // only sets isFromFormerCoach=true when a matched name was found).
  protected formerCoachBadgeBodyText(): string {
    const name = this.formerCoachName?.trim();
    if (!name) {
      this.logging.trackSilentProblem('edit-exercise: former-coach card missing coachName', { docId: this.exercise?._id });
      return LL.coach.formerCoachBadge.editPageBody({ coachName: DEFAULT_COACH_NAME_FALLBACK });
    }
    return LL.coach.formerCoachBadge.editPageBody({ coachName: name });
  }

  // For every active-plan subSlot whose exercise_id === fromId, rewrite to toId.
  // Shared by fork-on-save and revert. Persists only if any rewrite happened.
  //
  // This deliberately bypasses replaceExerciseInSubSlot — the canonical helper exists
  // for genuine SWAPS (plateau→similar, weakness rotation), where exercise-tied state
  // must reset because the user is moving to a different exercise. Fork and revert are
  // RENAMES: same exercise from the user's progression standpoint, just a different
  // id. exercisePaths / rebuildPathsForIntensity / lastTimeConcluded_utc /
  // plateauWorkoutLog_id all carry valid in-flight state that the user expects to
  // continue. Resetting them would silently restart periodization on the user-renamed
  // exercise, surprising the user. This is the same exemption pattern as the
  // custom-exercise-rename loops in user-db.service.ts (see exercise-replacement.md).
  // covers the optional string fields with cs-input/cs-textarea two-way bindings on
  // edit-exercise-page.html (description, shortname, videoUrl). If a future template
  // adds another bound optional string field, add it here too — otherwise the binding
  // asymmetry will silently re-introduce the false-fork bug for globals missing it.
  private normalizeBoundStringFields(ex: { description?: string; shortname?: string; videoUrl?: string } | undefined): void {
    if (!ex) return;
    if (ex.description === undefined) ex.description = '';
    if (ex.shortname === undefined) ex.shortname = '';
    if (ex.videoUrl === undefined) ex.videoUrl = '';
  }

  private async rewritePlanExerciseIdAsync(fromId: string, toId: string, transactionId: TransactionId): Promise<void> {
    const plan = this.userDb.fitnessPlan;
    if (!plan?.subSlots) return;
    let didRewrite = false;
    for (const subSlot of Object.values(plan.subSlots)) {
      if (subSlot.exercise_id === fromId) {
        // eslint-disable-next-line no-restricted-syntax -- fork/revert is a rename, not a swap; preserves periodization state (see exercise-replacement.md exemption sites)
        subSlot.exercise_id = toId;
        didRewrite = true;
      }
    }
    if (didRewrite) await this.userDb.savePlanAsync(transactionId);
  }

  protected handleViewSourceClick() {
    const sourceId = this.customExercise?.local?.sourceExerciseId;
    if (!sourceId) return;
    void this.navCtrl.pushAsync(IViewExercisePage, { id: sourceId });
  }

  // Revert is offered only when the exercise is a UDE fork (excludes import-self).
  // Not flag-gated: letting users undo existing forks even after the flag is disabled
  // is the safer default.
  protected canRevertToOriginal(): boolean {
    if (this.mode !== Mode.Custom) return false;
    return isForkUde(this.customExercise);
  }

  protected handleRevertToOriginal() {
    void this.handleRevertToOriginalAsync();
  }

  // "Based on:" card action slot: Revert to Original (when this is a revertable fork).
  // "Check for Update" is NOT here any more — a partial apply / ignore is treated as
  // resolved (no "pending" state), and the manual re-pull lives in the header ⋮ menu
  // (see `handleHeaderMenuClick`). Returns undefined when not revertable (omits the action).
  protected basedOnCardActionText(): TranslatedString | undefined {
    return this.canRevertToOriginal() ? LL.editExercise.buttons.revertToOriginal() : undefined;
  }

  protected handleBasedOnCardAction() {
    if (this.canRevertToOriginal()) {
      this.handleRevertToOriginal();
    }
  }

  // Header ⋮ menu visibility: anything that carries a `sourceExerciseId` and is NOT a coach
  // copy. Always available so the user can manually re-pull the source ("Check for Update")
  // even after a resolved partial apply / ignore; the "Update Available" banner is what
  // surfaces a genuinely-new rev. (Source resolvability/deletion is handled at click time in
  // openDiffPickerFromCheckForUpdate.) The test is `sourceExerciseId` and deliberately NOT
  // `isForkUde` — see `design-docs/public-exercises.md`, "That population really is the same
  // only since `df-3593dfcb9c81`".
  //
  // `exercise` is undefined until a load succeeds, and the template calls this from the
  // header, outside the `[loading]` content — so read `local` through the whole optional
  // chain, the way `exercise-stats.canCheckForUpdate` does.
  protected canCheckForUpdate(): boolean {
    const local = (this.exercise as CustomExercise | undefined)?.local;
    return this.mode === Mode.Custom
      && !!local?.sourceExerciseId
      && !local?.coachShared;
  }

  // Header ⋮ — opens a single-item "Check for Update" menu that re-pulls the source via
  // the same fresh-recheck-then-present flow the "Based on:" card used to host.
  protected async handleHeaderMenuClick(event: Event) {
    const buttons: PopupMenuButton[] = [{
      text: LL.editExercise.buttons.checkForUpdate(),
      handler: () => void this.openDiffPickerFromCheckForUpdate(),
    }];
    await this.popupMenuCtrl.createMenu({
      buttons,
      // currentTarget = the bound cs-icon-button (anchor the menu to the button, not a
      // child icon the click may have landed on); read synchronously before the await.
      targetFromElement: event.currentTarget as HTMLElement,
    });
  }

  private async handleRevertToOriginalAsync() {
    if (!this.auth.user?.uid) {
      this.toastController.showToast({
        message: LL.editExercise.toasts.saveFailed(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return;
    }
    const confirmed = await this.alertCtrl.showAlertAsync({
      title: LL.editExercise.alerts.revertToOriginal.title(),
      message: LL.editExercise.alerts.revertToOriginal.message(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.yes(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;

    const transactionId = DatabaseWrapper.getTransactionId();
    const thisUde = this.customExercise;
    const startId = thisUde.local?.sourceExerciseId;
    if (!startId) return;

    // The chain walk reads both databases and lets an unavailable one travel out. Catch it
    // HERE, because this method is started with `void` (handleRevertToOriginal) and nothing
    // above would catch it: the user would confirm the dialog and then see nothing happen at
    // all. Say we could not read rather than reusing the "no longer available" toast below —
    // telling somebody their original is gone when we simply could not look is the confusion
    // this whole change exists to remove. Nothing has been written yet at this point, so
    // returning leaves the exercise exactly as it was and a retry still works.
    let ancestorId: string | undefined;
    try {
      ancestorId = await findNearestLiveAncestorAsync(startId, this.userDb, this.exerciseDB, this.docWatcher);
    } catch (e) {
      // any read that failed lands here, not only an unavailable database: the curated read
      // in the walk throws every failure but a not-found (df-c21e11979ef2), and rethrowing
      // from this `void`-started method would reach nothing. An unavailable database is an
      // expected transient state; anything else is a surprise and must reach a debugger.
      if (!isDatabaseUnavailable(e)) {
        this.logging.trackSilentProblem('edit-exercise: revert ancestor walk failed', {
          startId, error: describeErrorForLog(e),
        });
      }
      this.toastController.showToast({
        message: LL.editExercise.toasts.couldNotReachExercises(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return;
    }

    if (!ancestorId) {
      this.toastController.showToast({
        message: LL.editExercise.toasts.originalNoLongerAvailable(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return;
    }

    try {
      await this.rewritePlanExerciseIdAsync(thisUde._id, ancestorId, transactionId);

      // commit the draft to the identity-map canonical, then mutate + save the canonical
      // so we don't bypass the identity map (which would cause _rev conflicts / Sentry noise)
      const canonical = this.exerciseDraft.commitDraft() as CustomExercise;
      canonical.virtuallyDeleted = true;
      await this.userDb.saveCustomExerciseAsync(canonical, transactionId);

      this.toastController.showToast({
        message: LL.editExercise.toasts.revertedToOriginal(),
        type: ToastType.SuccessLight,
        duration: 2000,
      });
      // pass ancestorId so the picker's returnCallback (onExerciseModified) sets
      // selectExerciseByIdWhenLoading + patches the local plan snapshot to point at
      // the ancestor (otherwise the picker shows neither Active badge nor selected
      // radio on the ancestor row until the user exits and re-enters the picker).
      // Picker's ionViewDidEnter also rebuilds hide-source set on entry, so the
      // source reappears cleanly without explicit refresh.
      await this.navCtrl.popAsync(ancestorId);
    } catch (err) {
      console.error('Revert failed', err);
      this.toastController.showToast({
        message: LL.editExercise.toasts.saveFailed(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
    }
  }

  // update detection for imported exercises — maps the shared {hasUpdate, original,
  // deleted} return into the page's banner state. Re-runnable: called on load AND on each
  // live coachExerciseApplied$, so it must be able to CLEAR a stale banner, not only set it.
  private async checkForOriginalUpdateAsync() {
    if (!this.customExercise.local?.sourceExerciseId) return;
    let result: ImportedExerciseUpdateResult;
    try {
      result = await this.exerciseDB.checkImportedExerciseForUpdateAsync(this.customExercise, this.docWatcher, this.resolveLocalSource);
    } catch (err: any) {
      // non-fatal: a failed check leaves the banner state unchanged. Surface so a systemic
      // failure (e.g. unexpected response shape) isn't invisible.
      console.error('edit-exercise: update-available check failed', err);
      this.logging.trackSilentProblem('edit-exercise: update-available check failed', {
        docId: this.customExercise._id, err: String(err?.message ?? err),
      });
      return;
    }
    if (this.viewDestroyed) return;
    if (result.deleted) {
      this.originalDeleted = true;
      this.hasOriginalUpdate = false;
      this.cdRef.markForCheck();
    } else if (result.original) {
      // source resolved: assign (not set-true) so a live re-detect CLEARS a stale banner
      // once the update was applied elsewhere. A network blip returns `original: null` —
      // leave the banner as-is rather than flicker it off. clear a prior deleted-state too:
      // the source came back (re-published), so the "Original No Longer Available" warning
      // must not linger now that the method is re-runnable.
      this.hasOriginalUpdate = result.hasUpdate;
      this.originalDeleted = false;
      this.cdRef.markForCheck();
    }
    // else: swallowed network blip (original null, not deleted) — nothing changed, no CD.
  }

  // tap on "Update Available" banner. Re-runs the rev-check (cached originalExercise
  // from page load may be stale by minutes/hours) then routes to one of three
  // branches: silent rev-bump (no remaining diff), source-deleted, or diff dialog.
  protected handleRefreshFromOriginal() {
    void this.handleRefreshFromOriginalDialogAsync();
  }

  // "Check for Update" manual re-pull from the header ⋮ menu (`handleHeaderMenuClick`).
  // Always available on a fork (a partial apply / ignore is resolved — no "pending"
  // state). Does its OWN fresh `checkImportedExerciseForUpdateAsync` call to populate
  // `originalExercise` — must NOT delegate to `handleRefreshFromOriginalDialogAsync`,
  // which would fall through to `setLastSeenSourceRevisionAsync` on the `hasUpdate: false`
  // case and silently advance the seen-rev. Disposition:
  //   - source deleted → originalNoLongerAvailable toast
  //   - source resolved AND the 3-way diff is non-empty → present picker (covers both a
  //     genuine `hasUpdate: true` and a re-pull where field values still differ)
  //   - source resolved AND diff empty → alreadyUpToDateToast (no picker, no
  //     `sourceRevision` bump — R3 lineage-safety invariant)
  // (The exercise-switch menu opens the picker IN PLACE via its own `handleCheckForUpdate`,
  // not by navigating here.)
  protected async openDiffPickerFromCheckForUpdate() {
    if (!this.customExercise.local?.sourceExerciseId) return;
    const result = await this.exerciseDB.checkImportedExerciseForUpdateAsync(this.customExercise, this.docWatcher, this.resolveLocalSource);
    if (this.viewDestroyed) return;
    if (result.deleted) {
      this.originalDeleted = true;
      this.hasOriginalUpdate = false;
      this.cdRef.markForCheck();
      this.toastController.showToast({
        message: LL.editExercise.toasts.originalNoLongerAvailable(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return;
    }
    if (!result.original) {
      // `checkImportedExerciseForUpdateAsync` swallows network errors as
      // `{ original: null, deleted: false }` (see exercise-db.service.ts). Show an
      // offline-explicit toast — DON'T pretend the doc is up-to-date when we couldn't
      // actually check.
      this.toastController.showToast({
        message: LL.editExercise.toasts.checkForUpdateOffline(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
      return;
    }
    if (result.hasUpdate) {
      this.hasOriginalUpdate = true;
      this.cdRef.markForCheck();
    }
    // Build the diff against the just-fetched source directly — `hasUpdate` may be
    // false (already-seen rev, partial-apply / ignore re-entry) but field values may
    // still differ. The picker shows whatever is still different.
    const diff = this.computeDiffAgainstOriginal(result.original);
    if (diff.length === 0) {
      // empty re-entry diff → no picker, no `sourceRevision` bump (R3 lineage
      // safety). The fork's content matches the source field-for-field; the user has
      // nothing to review.
      this.toastController.showToast({
        message: LL.editExercise.alerts.updateAvailable.alreadyUpToDateToast(),
        type: ToastType.SuccessLight,
        duration: 2000,
      });
      return;
    }
    await this.presentDiffDialogAsync(diff, result.original);
  }

  private async handleRefreshFromOriginalDialogAsync() {
    const result = await this.exerciseDB.checkImportedExerciseForUpdateAsync(this.customExercise, this.docWatcher, this.resolveLocalSource);
    if (this.viewDestroyed) return;
    if (result.deleted) {
      this.originalDeleted = true;
      this.hasOriginalUpdate = false;
      this.cdRef.markForCheck();
      return;
    }
    if (!result.hasUpdate) {
      // page-load rev-check said hasUpdate, but re-check now reports revs match
      // (or were already seen via another tab's picker review). Could mean: another
      // tab applied the update, another tab partial-applied (lastSeenSourceRevision
      // advanced but sourceRevision did not — see R3), the source was reverted to
      // an older rev, or the page-load check raced an in-flight server publish.
      // Either way, no banner to show here — clear banner state silently by setting
      // `lastSeenSourceRevision` only (NEVER `sourceRevision`: the fork may not have
      // actually re-derived from this rev, and R3's lineage-safety invariant forbids
      // bumping `sourceRevision` in a partial-apply / ignore situation).
      // (The "rev advanced but no user-visible diff" case is handled separately
      // by presentDiffDialogAsync's empty-diff branch — which IS a deliberate
      // acceptance and bumps both revs via bumpLocalSourceRevisionAsync.)
      if (result.original) {
        await this.setLastSeenSourceRevisionAsync(result.original._rev);
      } else {
        this.hasOriginalUpdate = false;
      }
      if (this.viewDestroyed) return;
      this.cdRef.markForCheck();
      this.toastController.showToast({
        message: LL.editExercise.alerts.updateAvailable.alreadyUpToDateToast(),
        type: ToastType.SuccessLight,
        duration: 2000,
      });
      return;
    }
    // hasUpdate: true — refresh the cached original to the just-fetched copy and
    // open the diff dialog
    const diff = this.computeDiffAgainstOriginal(result.original);
    await this.presentDiffDialogAsync(diff, result.original);
  }

  // Resolve display names for equipment + similar-exercise IDs, then delegate
  // to the pure helper. The caller-side resolvers are needed because the helper
  // is in a non-Angular service and shouldn't reach into i18n / SettingsService.
  // Build the structured per-field diff via the shared `exercise-diff-ui` helper. Only the
  // page-specific `similarExerciseId`-name lookup (its already-loaded exercise lists) stays
  // here; the equipment + i18n wiring lives in the shared helper.
  private computeDiffAgainstOriginal(original: CustomExercise): ExerciseDiffEntry[] {
    return buildLabeledCoachUpdateDiff(this.customExercise, original, this.settings.locale, id => {
      if (!id) return LL.editExercise.alerts.updateAvailable.empty();
      const ex = this.allExercises?.find(e => e._id === id) || this.allCustomExercises?.find(e => e._id === id);
      return ex?.name || id;
    });
  }

  // Build the diff dialog body and present it. Two presentation shapes depending
  // on whether there's a renderable diff:
  //   - non-empty diff: push the per-field `ExerciseDiffPage` modal (R2). Its result
  //     (`{ action: 'apply', appliedFields }` / `{ action: 'ignore' }` / undefined=cancel)
  //     comes back via the `onDiffPickerResult` returnCallback, which runs the R3 apply table.
  //   - empty diff (server _rev advanced but no user-visible field change):
  //     "Update Revision" / "Cancel" AlertController — kept as-is; there is nothing
  //     to pick. Update Revision bumps `sourceRevision` (= `lastSeenSourceRevision`,
  //     R3 — no carry-over) silently; Cancel leaves it as-is.
  private async presentDiffDialogAsync(diff: ExerciseDiffEntry[], source: CustomExercise) {
    const freshRev = source._rev;
    // a resolved source doc always carries a `_rev`; guard narrows the type for the
    // rev-writing callees below (bump / setLastSeen) and is a no-op in practice.
    if (!freshRev) return;
    if (diff.length === 0) {
      // empty-diff branch unchanged from Phase 1 — picker is not the right vehicle
      // for "nothing to pick"
      const accept = await this.alertCtrl.showAlertAsync<boolean>({
        title: LL.editExercise.alerts.updateAvailable.title(),
        messageMarkdown: LL.editExercise.alerts.updateAvailable.noVisibleChangesBody(),
        buttons: [
          { text: LL.editExercise.alerts.updateAvailable.cancelButton(), role: AlertButtonRole.Cancel, result: false },
          { text: LL.editExercise.alerts.updateAvailable.updateRevisionButton(), role: AlertButtonRole.Primary, result: true },
        ],
      });
      if (this.viewDestroyed) return;
      if (!accept) return;
      // empty-diff Update Revision is a "treat this rev as fully accepted" silent bump —
      // sourceRevision AND lastSeenSourceRevision both move to the new rev.
      await this.bumpLocalSourceRevisionAsync(freshRev);
      return;
    }

    // R2 — present the per-field diff on the ExerciseDiffPage modal. It returns the user's
    // choice via the `onDiffPickerResult` returnCallback below; stash the apply context across
    // the push (the page hands back only the picked action).
    this.pendingDiffApply = { source, freshRev, diffLength: diff.length };
    void this.navCtrl.pushAsync(IExerciseDiffPage, {
      returnCallback: 'onDiffPickerResult',
      local: { diff },
    });
  }

  // returnCallback from the ExerciseDiffPage modal (edit-page draft flow). Applies through the
  // draft pipeline (applySelectedFieldsFromSourceAsync) — NOT the canonical-level apply used by
  // exercise-switch / exercise-stats. `result` is undefined on a back/close = cancel: nothing
  // is written, the banner stands (the update is only resolved by Apply or Ignore).
  async onDiffPickerResult(_context: unknown, result?: ExerciseDiffResult): Promise<void> {
    const pending = this.pendingDiffApply;
    this.pendingDiffApply = null;
    if (!pending || !result || this.viewDestroyed) return;
    const { source, freshRev, diffLength } = pending;
    if (result.action === 'apply') {
      const appliedFields = result.appliedFields;
      const isFullApply = appliedFields.size === diffLength;
      await this.applySelectedFieldsFromSourceAsync(source, appliedFields, isFullApply);
      if (this.viewDestroyed) return;
      this.toastController.showToast({
        message: isFullApply
          ? LL.editExercise.toasts.exerciseRefreshed()
          : LL.editExercise.toasts.partialUpdateApplied({ count: appliedFields.size }),
        type: ToastType.SuccessLight,
        duration: 2000,
      });
    } else if (result.action === 'ignore') {
      // Explicit "Ignore" — mark this rev seen (lastSeenSourceRevision = source rev). The
      // auto-banner / dot stops firing for this rev (the fork is treated as resolved); the
      // always-available "Check for Update" still lets the user re-pull it. `sourceRevision`
      // is never touched (lineage-safety invariant).
      await this.setLastSeenSourceRevisionAsync(freshRev);
    }
  }

  // R3 — set `local.lastSeenSourceRevision` and clear the banner state without touching
  // `local.sourceRevision`. Called from (a) the picker's explicit Ignore branch (mark the
  // rev seen → the fork reads as resolved) and (b) the banner-tap recheck's `!hasUpdate`
  // branch (page-load said update, recheck says revs match / already-seen) to silently
  // clear a now-stale banner. NOT used for partial apply (that sets lastSeenSourceRevision
  // inline in the selective applier). Same canonical-mutation shape as
  // bumpLocalSourceRevisionAsync (read identity-map canonical, mutate, save, mirror onto
  // draft + baseline) so isModified stays correct and a later commitDraft doesn't overwrite
  // the write.
  private async setLastSeenSourceRevisionAsync(newRev: string) {
    if (!this.customExercise.local) return;
    const canonical = await this.userDb.getCustomExerciseByIdAsync(this.exerciseId);
    if (!canonical?.local) return;
    canonical.local.lastSeenSourceRevision = newRev;
    await this.userDb.saveCustomExerciseAsync(canonical, DatabaseWrapper.getTransactionId());
    if (this.viewDestroyed) return;
    this.customExercise.local.lastSeenSourceRevision = newRev;
    const initial = this.exerciseInitial as CustomExercise;
    if (initial?.local) {
      initial.local.lastSeenSourceRevision = newRev;
    }
    this.hasOriginalUpdate = false;
    this.cdRef.markForCheck();
  }

  // Bump only local.sourceRevision on the canonical so the banner clears and
  // the next rev-check sees a fresh baseline. This path runs when revs match or
  // when there's no user-visible diff — neither warrants persisting the user's
  // in-progress unsaved form edits. Mutating the canonical via commitDraft
  // would do exactly that, so we bypass the draft pipeline:
  //   1. Read the canonical from the userDb identity map.
  //   2. Mutate canonical.local.sourceRevision and save.
  //   3. Mirror the bump onto the draft + exerciseInitial so a future commitDraft
  //      doesn't overwrite the new rev with the pre-bump value, and isModified
  //      stays whatever it was before (this bump is a no-op for the user).
  private async bumpLocalSourceRevisionAsync(newRev: string) {
    if (!this.customExercise.local) return;
    const canonical = await this.userDb.getCustomExerciseByIdAsync(this.exerciseId);
    if (!canonical?.local) return;
    canonical.local.sourceRevision = newRev;
    // R3 — keep lastSeenSourceRevision in lockstep on a "full-equivalent" bump so the
    // fork reads as fully in sync (both revs equal — dot off, nothing left to apply).
    canonical.local.lastSeenSourceRevision = newRev;
    await this.userDb.saveCustomExerciseAsync(canonical, DatabaseWrapper.getTransactionId());
    if (this.viewDestroyed) return;
    // mirror the bump onto draft + baseline so commitDraft + isModified stay correct
    this.customExercise.local.sourceRevision = newRev;
    this.customExercise.local.lastSeenSourceRevision = newRev;
    const initial = this.exerciseInitial as CustomExercise;
    if (initial?.local) {
      initial.local.sourceRevision = newRev;
      initial.local.lastSeenSourceRevision = newRev;
    }
    this.hasOriginalUpdate = false;
    this.cdRef.markForCheck();
  }

  // R3 / Supporting work — single source of apply truth.
  //
  // Selectively copy a subset of fields from a resolved source onto the working
  // exercise, write the R3 apply-table `local.*` fields directly (this method owns
  // them — there is no other caller doing it), then save via the same draft →
  // canonical → save → rebuild-draft → reset-baseline → somethingChanged() sequence
  // the legacy `applyRefreshFromOriginalAsync` used.
  //
  // Three behavioural rules locked in by Step 5:
  //   1. The R3 apply-table writes happen HERE inline (not in the caller): always
  //      advance `lastSeenSourceRevision` to the source rev; advance `sourceRevision`
  //      only on full apply (`isFullApply` true). The caller passes `isFullApply`
  //      because picker-dismiss is the only place that knows whether every diffed
  //      field was checked.
  //   2. Primitive fields copy by value. Non-primitive fields (`standard`, typed `any`)
  //      MUST be deep-cloned — a reference copy would alias the staging doc's nested
  //      object into `userdb-*` and a later mutation could corrupt either side.
  //   3. The draft-lifecycle sequence (commitDraft → save → new draft → reset
  //      `exerciseInitial` → somethingChanged) is load-bearing for `isModified` /
  //      unsaved-edit handling; preserve it verbatim regardless of which fields were
  //      copied.
  //
  // Open Decision 2 (carrier-field parity): the field universe is exactly
  // `ExerciseDiffField`. Any future-added `CustomExercise` field that's neither in
  // `ExerciseDiffField` nor in `local`/`usedInSlots`/server-stamped (`authorHash`/
  // `authorIdentityHash`) would silently stop being refreshed on full-apply — the
  // parity unit test must enumerate the full `CustomExercise` field set and the
  // executor whitelists / explicitly preserves each.
  private async applySelectedFieldsFromSourceAsync(
    source: CustomExercise,
    appliedFields: Set<ExerciseDiffField>,
    isFullApply: boolean,
  ): Promise<void> {
    const sourceRev = source._rev;
    if (!sourceRev) return; // a resolved source always has a _rev; satisfies the typed applier
    const loading = this.loadingCtrl.create();
    void loading.present();
    try {
      // Copy picked fields onto the working DRAFT + stamp the R3 apply-table revs via
      // the shared applier (services/exercise-fork-update.ts) — same field-copy +
      // deep-clone-`standard` + rev-stamp logic the in-place exercise-switch flow uses.
      // The draft-lifecycle sequence below (commitDraft → save → rebuild) is the only
      // edit-page-specific part; it preserves isModified / unsaved-edit handling.
      const working = this.exercise as CustomExercise;
      applyDiffFieldsToCanonical(working, source, appliedFields, sourceRev, isFullApply);

      // save via draft → canonical to preserve identity-map object — same shape as
      // the legacy applyRefreshFromOriginalAsync. Draft-lifecycle sequence below is
      // load-bearing for isModified / unsaved-edit handling.
      const canonical = this.exerciseDraft.commitDraft();
      await this.userDb.saveCustomExerciseAsync(canonical as CustomExercise, DatabaseWrapper.getTransactionId());
      if (this.viewDestroyed) { void loading.dismiss(); return; }

      void loading.dismiss();
      this.hasOriginalUpdate = false;

      // rebuild draft from canonical (identity-map object), not the old draft
      this.exerciseDraft = new CouchDocDraft(canonical);
      this.exercise = this.exerciseDraft.draft;
      this.exerciseInitial = deepClone(this.exercise);
      this.somethingChanged();
    } catch (err: any) {
      void loading.dismiss();
      console.error('Apply selected fields from source failed', err);
      // Surface to Sentry without escalating to the abnormal-exception toast — the
      // user-friendly refreshFailed toast is the appropriate UX, but a missing Sentry
      // event would leave prod failures (PouchDB conflict, network mid-save, draft
      // invariant violation) invisible to anyone watching telemetry.
      this.logging.trackSilentProblem('applySelectedFieldsFromSourceAsync failed', {
        exerciseId: this.exerciseId,
        err: String(err?.message ?? err),
        stack: err?.stack,
      });
      this.toastController.showToast({
        message: LL.editExercise.toasts.refreshFailed(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
    }
  }

  previewDescriptionButtonClick() {
    if (this.createMode) {
      let description = this.exercise.description || '';
      // if video URL, prepend as Markdown image so cs-markdown renders it as an embed
      if (this.exercise.videoUrl) {
        description = `![Demo](${this.exercise.videoUrl})\n\n${description}`;
      }
      // create mode previews the custom exercise the user is typing right now,
      // so both values are their own words. there is no app copy here to route
      // through the translation catalog.
      let previewHelpText: PreviewHelpText = {
        title: untranslatedString(this.exercise.name),
        text: untranslatedString(description),
      }
      window.localStorage.setItem('previewHelpText', JSON.stringify(previewHelpText));
      void this.navCtrl.pushAsync(IHelpCardPage, {});
    } else {
      void this.navCtrl.pushAsync(IHelpCardPage, {
        helpId: '@instructions:' + this.exercise._id,
      });
    }
  }

  loader: Loading;
  @ViewChild('descriptionTextarea') descriptionRef: ElementRef;

  onImageUploadEvent(event: Event) {
    void this.onImageUploadEventAsync(event);
  }
  async onImageUploadEventAsync(event: Event) {
    const target = event.target as HTMLInputElement;
    const fileName = this.exercise._id;

    this.loader = this.loadingCtrl.create();
    void this.loader.present();
    // TODO: this is not properly async!!!!
    this.imageService.processUploadImages(
      target.files,
      fileName,
      img => this.addImageToDescription(img),
      () => this.uploadFailed(),
      true,
    );

    // Resets file input so you can reuse the same image
    // Solution with good browser support:
    // https://stackoverflow.com/questions/20549241/how-to-reset-input-type-file
    target.value = '';
    if (!/safari/i.test(navigator.userAgent)) {
      target.type = '';
      target.type = 'file';
    }
  }

  uploadFailed() {
    void this.loader.dismiss();
    this.cdRef.markForCheck();
  }

  addImageToDescription(image: ScaledImage) {
    const cursorIndex = this.descriptionRef.nativeElement.selectionStart;
    const markdownTag = this.imageService.getMarkdownTag(image);

    this.exercise.description =
      this.exercise.description.substring(0, cursorIndex) + markdownTag + this.exercise.description.substring(cursorIndex);

    this.somethingChanged();

    void this.loader.dismiss();
    this.cdRef.markForCheck();
  }

  addImageButtonClick() {
    document.getElementById('hidden-input').click();
  }

  addStandardAbsolute() {
    this.exercise.standard = new StrengthStandard_Absolute();
    this.addStandardRow(this.exercise.standard.men);
    this.addStandardRow(this.exercise.standard.women);

    this.somethingChanged();
  }

  addStandardRelative() {
    this.exercise.standard = new StrengthStandard_Relative();

    this.somethingChanged();
  }

  removeStandardRelative() {
    this.exercise.standard = null;

    this.somethingChanged();
  }

  addStandardRow(a: StrengthPerBW[]) {
    a.push(new StrengthPerBW());

    this.somethingChanged();
  }

  removeStandardRow(a: StrengthPerBW[], perBW: StrengthPerBW) {
    if (isStrengthStandardRelative(this.exercise.standard)) {
      // this never happens, but keeps the compiler happy about exact type below
      return;
    }

    // remove the given row
    let i = a.indexOf(perBW);
    a.splice(i, 1);

    // if no data left
    if (this.exercise.standard.men.length == 0 && this.exercise.standard.women.length == 0) {
      // remove the standard
      this.exercise.standard = null;
    }

    this.somethingChanged();
  }

  useRMTestingChanged(useRMTesting: boolean) {
    if (useRMTesting) {
      delete this.exercise.avoidRMTesting;
    } else {
      this.exercise.avoidRMTesting = true;
    }
    this.somethingChanged();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.coachAppliedSub?.unsubscribe();
    this.coachAppliedSub = null;
  }
}
